'use client';

import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useParams } from 'next/navigation';
import Link from 'next/link';
import { motion } from 'framer-motion';
import {
  Type,
  BookOpen,
  GraduationCap,
  Headphones,
  MessagesSquare,
  Newspaper,
  HelpCircle,
  BarChart3,
  ArrowLeft,
  PenLine,
  Bot,
  NotebookPen,
  Route,
  Dumbbell,
  Languages,
  SpellCheck,
  PenTool,
  ChevronRight,
  Flame,
  Zap,
  Heart,
  Target,
} from 'lucide-react';
import { languageApi } from '@/lib/language-api';
import type { LanguageOverview } from '@/types/language';
import type { PracticeStateDto } from '@/lib/language-api';
import { EmptyState, useLangUser } from '@/components/language/primitives';
import { useMotion, useCountUp } from '@/lib/motion';
import s from './hub.module.css';

/**
 * The skills, in learning order.
 *
 * No per-skill colour: the old hub gave every family its own tinted tile and
 * the page read as confetti. Hierarchy now comes from type, not hue — see
 * hub.module.css.
 *
 * The old `n: 1..12` is gone. It was the tile's own position rendered into its
 * corner: a number that answered a question nobody asked.
 */
const SECTIONS = [
  { key: 'alphabet', label: 'Bảng chữ cái', desc: 'Chữ cái & phát âm', icon: Type, hue: '#6366f1' },
  { key: 'hanzi', label: 'Luyện viết chữ Hán', desc: 'Nét mẫu, tô theo & viết', icon: PenTool, hue: '#e11d48' },
  { key: 'vocab', label: 'Từ vựng', desc: 'Học từ theo chủ đề', icon: BookOpen, hue: '#10b981' },
  { key: 'grammar', label: 'Ngữ pháp', desc: 'Cấu trúc câu', icon: GraduationCap, hue: '#f59e0b' },
  { key: 'listening', label: 'Nghe', desc: 'Luyện nghe & shadowing', icon: Headphones, hue: '#0ea5e9' },
  { key: 'conversation', label: 'Giao tiếp', desc: 'Hội thoại hằng ngày', icon: MessagesSquare, hue: '#ec4899' },
  { key: 'reading', label: 'Đọc', desc: 'Bài đọc & báo', icon: Newspaper, hue: '#f97316' },
  { key: 'qna', label: 'Q&A', desc: 'Câu hỏi thường gặp', icon: HelpCircle, hue: '#14b8a6' },
  { key: 'writing', label: 'Luyện viết', desc: 'AI chữa bài & chấm điểm', icon: PenLine, hue: '#8b5cf6' },
  { key: 'roleplay', label: 'Hội thoại AI', desc: 'Nhập vai tình huống', icon: Bot, hue: '#d946ef' },
  { key: 'translate', label: 'Dịch văn bản', desc: 'Dịch 2 chiều & giải thích', icon: Languages, hue: '#3b82f6' },
  { key: 'grammar-check', label: 'Kiểm tra ngữ pháp', desc: 'Soi lỗi, chấm & sửa', icon: SpellCheck, hue: '#22c55e' },
] as const;

// Kanji/hanzi only exist in Japanese and Chinese — showing the tile on English
// would promise a page with nothing in it.
const CJK_ONLY = new Set(['hanzi']);
const CJK_CODES = new Set(['ja', 'zh']);

const AI_SECTIONS = new Set(['writing', 'roleplay', 'translate', 'grammar-check']);

/** One stat, counted up on mount. Plain numbers — no emoji bubbles. */
function Stat({ value, label, icon, hue }: { value: number; label: string; icon: React.ReactNode; hue: string }) {
  const n = useCountUp(value);
  return (
    <div className={s.stat} style={{ '--c': hue } as CSSProperties}>
      <div className={s.statValue}><span className={s.statIcon}>{icon}</span>{n}</div>
      <div className={s.statLabel}>{label}</div>
    </div>
  );
}

/** Roadmap / Practice — the two primary entries. */
function StartCard({ href, icon, title, desc, from, to }: {
  href: string; icon: React.ReactNode; title: string; desc: string;
  /** Hai đầu gradient của ô icon — mỗi lối vào một màu để nhận ra từ xa. */
  from: string; to: string;
}) {
  return (
    <Link href={href} className={s.card} style={{ '--c1': from, '--c2': to } as CSSProperties}>
      <span className={s.cardIcon}>{icon}</span>
      <div className="min-w-0">
        <h3 className={s.cardTitle}>{title}</h3>
        <p className={s.cardDesc}>{desc}</p>
      </div>
      <ChevronRight size={18} className={s.chev} />
    </Link>
  );
}

/** A labelled group of skills, laid out as a table of contents. */
function SkillSection({ title, note, items, code, counts, loading, m, isAi }: {
  title: string;
  note?: string;
  items: ReadonlyArray<(typeof SECTIONS)[number]>;
  code: string;
  counts: Record<string, number> | undefined;
  loading: boolean;
  m: ReturnType<typeof useMotion>;
  isAi?: boolean;
}) {
  if (!items.length) return null;
  return (
    <motion.section variants={m.childEnter} className={s.section}>
      <div className={s.sectionHead}>
        <h2 className={s.h2}>{title}</h2>
        {note && <span className={s.sectionNote}>{note}</span>}
      </div>
      <div className={s.list}>
        {items.map((sec, i) => {
          const Icon = sec.icon;
          const count = counts?.[sec.key] ?? 0;
          // Kỹ năng chưa có nội dung: vẫn bấm vào được (trang con tự giải thích),
          // nhưng mờ đi và ghi "Sắp có" thay cho "0 mục" — một hàng "0 mục" trông
          // như trang hỏng, còn "Sắp có" nói đúng sự thật.
          const trong = !isAi && !loading && count === 0;
          return (
            <Link
              key={sec.key}
              href={`/language/${code}/${sec.key}`}
              className={s.row}
              data-trong={trong || undefined}
              style={{ '--c': sec.hue, '--i': i } as CSSProperties}
            >
              <span className={s.rowIcon}>
                <Icon size={19} strokeWidth={1.75} />
              </span>
              <div className={s.rowBody}>
                <h3 className={s.rowTitle}>{sec.label}</h3>
                <p className={s.rowDesc}>{sec.desc}</p>
              </div>
              {isAi ? (
                <span className={s.proTag}>PRO</span>
              ) : (
                <span className={s.rowMeta}>{loading ? '…' : trong ? 'Sắp có' : `${count.toLocaleString('vi-VN')} mục`}</span>
              )}
              <ChevronRight size={16} className={s.chev} />
            </Link>
          );
        })}
      </div>
    </motion.section>
  );
}

export default function LanguageHomePage() {
  const params = useParams();
  const code = String(params.code);
  const { isAuthenticated } = useLangUser();
  const [lang, setLang] = useState<LanguageOverview | null>(null);
  const [due, setDue] = useState(0);
  const [st, setSt] = useState<PracticeStateDto | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    languageApi
      .overview(code)
      .then((res) => alive && setLang(res.data.data ?? null))
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [code]);

  useEffect(() => {
    if (!isAuthenticated) return;
    languageApi
      .reviewQueue(code)
      .then((res) => setDue(res.data.data?.count ?? 0))
      .catch(() => {});
    // Streak/XP/hearts for the hero chips. This reuses the Practice endpoint
    // rather than adding one for three numbers — it over-fetches the lesson
    // tree, but it is the same call the learner triggers one tap later, and a
    // second endpoint returning a subset of an existing one is a second thing
    // to keep in sync. Non-blocking: the chips appear when it lands.
    languageApi
      .practice(code)
      .then((res) => setSt(res.data.data?.state ?? null))
      .catch(() => {});
  }, [code, isAuthenticated]);

  const m = useMotion();
  const counts = useMemo(() => (lang?.counts ?? undefined) as Record<string, number> | undefined, [lang]);

  // Hanzi only exists in Japanese and Chinese — showing the tile on English
  // would promise a page with nothing in it.
  const visible = useMemo(
    () => SECTIONS.filter((sec) => !CJK_ONLY.has(sec.key) || CJK_CODES.has(code)),
    [code],
  );
  const learnSections = useMemo(() => visible.filter((sec) => !AI_SECTIONS.has(sec.key)), [visible]);
  const aiSections = useMemo(() => visible.filter((sec) => AI_SECTIONS.has(sec.key)), [visible]);

  if (!loading && !lang) {
    return (
      <div className={s.root}>
        <div className={s.notFound}>
          <EmptyState emoji="🔎" title="Không tìm thấy ngôn ngữ" hint="Ngôn ngữ này chưa tồn tại hoặc đã bị ẩn." />
          <div className="mt-6">
            <Link href="/language" className={s.notFoundLink}>
              ← Về danh sách ngôn ngữ
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const name = lang?.name ?? code.toUpperCase();
  const heading = st && st.streak > 0 ? `Ngày thứ ${st.streak} liên tiếp.` : 'Hôm nay học gì?';
  const lede = due > 0
    ? `Có ${due} thẻ từ vựng đến hạn ôn. Ôn trước, rồi học tiếp bài mới — trí nhớ giữ lâu hơn theo thứ tự đó.`
    : 'Đi theo lộ trình nếu muốn có người dẫn đường, hoặc chọn thẳng một kỹ năng bên dưới.';

  // padding-top on .root is deliberate: the bar below should abut the nav, and
  // the notch is already covered by .app-main.
  return (
    <div className={s.root}>
      {/* The STUCK position is the part that needs the nav's real height. A
          sticky element resolves `top` against the scrollport, so it never sees
          .app-main's safe-area padding: at a hard top-16 this bar parked BEHIND
          the nav on a notched PWA, where the nav is 4rem + inset. */}
      <div className={s.bar}>
        <div className={s.barInner}>
          <Link href="/language" className={s.back} aria-label="Quay lại">
            <ArrowLeft size={18} />
          </Link>
          <span className="text-lg leading-none">{lang?.flagEmoji ?? '🏳️'}</span>
          <span className={s.barTitle}>{name}</span>
          {isAuthenticated && (
            <>
              <Link href={`/language/notebook?code=${code}`} className={s.barLink}>
                <NotebookPen size={16} /> <span className={s.barLinkText}>Sổ tay</span>
              </Link>
              <Link href={`/language/${code}/stats`} className={s.barLink}>
                <BarChart3 size={16} /> <span className={s.barLinkText}>Thống kê</span>
              </Link>
            </>
          )}
        </div>
      </div>

      <motion.div variants={m.pageEnter} initial="hidden" animate="show" className={s.wrap}>
        {/* ── Đầu trang: tiến độ của mình trước, rồi mới đến việc phải làm. ── */}
        <motion.header variants={m.childEnter} className={s.head}>
          <div className="min-w-0">
            <p className={s.eyebrow}>{name} · Khoá của bạn</p>
            <h1 className={s.h1}>{heading}</h1>
            <p className={s.lede}>{lede}</p>
          </div>
          {isAuthenticated && st && (
            <div className={s.stats}>
              <Stat value={st.streak} label="ngày liên tiếp" hue="#f97316" icon={<Flame size={18} />} />
              <Stat value={st.xp} label="điểm XP" hue="#eab308" icon={<Zap size={18} />} />
              <Stat value={st.hearts} label="tim còn lại" hue="#ef4444" icon={<Heart size={17} />} />
            </div>
          )}
        </motion.header>

        {/* Ôn tập + Lộ trình + Luyện tập — the ways IN. Bigger than the skill
            rows on purpose: a hub with fourteen equal entries has no entry point. */}
        <motion.section variants={m.childEnter} className={s.section}>
          <div className={s.sectionHead}>
            <h2 className={s.h2}>Bắt đầu</h2>
          </div>
          {isAuthenticated && due > 0 && (
            <div className={s.review}>
              <span className={s.reviewFlame} aria-hidden><Flame size={24} /></span>
              <div className="min-w-0 flex-1">
                <p className={s.reviewTitle}>{due} thẻ đến hạn ôn tập</p>
                <p className={s.reviewDesc}>Lặp lại ngắt quãng — ôn đúng lúc sắp quên để nhớ lâu.</p>
              </div>
              <Link href={`/language/${code}/vocab?mode=review`} className={s.primaryBtn}>
                Ôn ngay <ChevronRight size={16} />
              </Link>
            </div>
          )}
          <div className={s.startGrid}>
            <StartCard
              href={`/language/${code}/roadmap`}
              icon={<Route size={22} strokeWidth={1.75} />}
              title="Lộ trình học"
              desc="Đi từng chặng từ cơ bản đến nâng cao"
              from="#6366f1"
              to="#8b5cf6"
            />
            <StartCard
              href={`/language/${code}/practice`}
              icon={<Dumbbell size={22} strokeWidth={1.75} />}
              title="Luyện tập"
              desc="Bài tập ngắn mỗi ngày, tính XP và chuỗi ngày"
              from="#10b981"
              to="#14b8a6"
            />
            {/* IELTS sống ở đây, không ở /tech-trends nữa: một chỗ học tiếng Anh. */}
            {/* Khoá theo giáo trình của trường: Dekiru Nihongo (JPD113/JPD123). */}
            {/* 05/10/2026: khoá JP tự học 0 → N1 và CH 0 → HSK 6 (cùng khung với IELTS). */}
            {code === 'ja' && (
              <StartCard
                href="/language/ja/jp"
                icon={<Target size={22} strokeWidth={1.75} />}
                title="JP · Từ 0 đến N1"
                desc="Lộ trình JLPT tự học: kana, hội thoại, ngữ pháp, chữ Hán, nghe, nói — luyện nói cùng CuongMini"
                from="#db2777"
                to="#8b5cf6"
              />
            )}
            {code === 'zh' && (
              <StartCard
                href="/language/zh/ch"
                icon={<Target size={22} strokeWidth={1.75} />}
                title="CH · Từ 0 đến HSK 6"
                desc="Pinyin, thanh điệu, hội thoại, chữ Hán (xem nét, tập viết), nghe, nói — luyện nói cùng CuongMini"
                from="#dc2626"
                to="#f59e0b"
              />
            )}
            {code === 'ja' && (
              <StartCard
                href="/language/ja/dekiru"
                icon={<BookOpen size={22} strokeWidth={1.75} />}
                title="できる日本語 · Bài 0–15"
                desc="Học từ con số 0 theo sách trên lớp: kana, ngữ pháp, từ vựng, nói, nghe"
                from="#e11d48"
                to="#f97316"
              />
            )}
            {code === 'en' && (
              <StartCard
                href="/language/en/ielts"
                icon={<Target size={22} strokeWidth={1.75} />}
                title="IELTS nền tảng"
                desc="15 ngày từ con số 0, có gia sư AI giảng từng trang"
                from="#f97316"
                to="#ef4444"
              />
            )}
          </div>
        </motion.section>

        {/* Two sections, because a lesson and an AI tool are different promises:
            one has content waiting, the other needs Pro and writes something new. */}
        <SkillSection
          title="Kỹ năng"
          note={`${learnSections.length} phần`}
          items={learnSections}
          code={code}
          counts={counts}
          loading={loading}
          m={m}
        />
        <SkillSection
          title="Công cụ AI"
          note="Dành cho gói Pro"
          items={aiSections}
          code={code}
          counts={counts}
          loading={loading}
          m={m}
          isAi
        />
      </motion.div>
    </div>
  );
}
